'use client';

import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Navbar } from '@/components/navbar';
import { Footer } from '@/components/footer';
import { CheckCircle2, FileText, Home, Shield, Clock } from 'lucide-react';
import { motion } from 'framer-motion';

export default function InspectionsPage() {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.8, ease: "easeOut" },
    },
  };

  const inspectionTypes = [
    {
      id: 'pre-purchase',
      title: 'Pre-Purchase Inspection',
      description: 'Comprehensive inspection before buying your home',
      price: '$450',
      features: [
        'Structural assessment',
        'Roof and foundation evaluation',
        'Electrical system review',
        'Plumbing inspection',
        'HVAC system check',
        'Detailed written report',
        'Photo documentation'
      ]
    },
    {
      id: 'pre-sale',
      title: 'Pre-Sale Inspection',
      description: 'Know your home\'s condition before listing',
      price: '$450',
      features: [
        'Complete home assessment',
        'Identify issues upfront',
        'Listing advantage',
        'Professional report',
        'Buyer confidence',
        'Negotiation support',
        'Professional photos'
      ]
    },
    {
      id: 'insurance',
      title: 'Insurance Inspection',
      description: 'Required inspection for insurance purposes',
      price: '$350',
      features: [
        'Insurance-standard inspection',
        'Safety compliance check',
        'Claim documentation',
        'Property assessment',
        'Condition report',
        'Professional certification',
        'Quick turnaround'
      ]
    }
  ];

  const whyChooseItems = [
    {
      icon: CheckCircle2,
      title: 'Certified Professionals',
      description: 'Licensed, insured, and certified home inspectors'
    },
    {
      icon: FileText,
      title: 'Detailed Reports',
      description: 'Comprehensive documentation with photos and recommendations'
    },
    {
      icon: Clock,
      title: 'Efficient Service',
      description: '2-hour standard inspection with prompt reporting'
    },
    {
      icon: Shield,
      title: 'Your Protection',
      description: 'Thorough assessment to protect your investment'
    }
  ];

  return (
    <>
      <Navbar data-zylo-id="zylo-4a9a1d56210d" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="106:7-106:17" data-zylo-component="InspectionsPage" />

      {/* Hero Section */}
      <section data-zylo-id="zylo-5722c5f1e427" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="109:7-165:17" data-zylo-component="InspectionsPage" className="relative overflow-hidden bg-gradient-to-b from-secondary/10 via-white to-white pt-20 pb-16 sm:pt-32 sm:pb-24">
        <div data-zylo-id="zylo-3861f3f0c89c" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="110:9-164:15" data-zylo-component="InspectionsPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-b067344bfac5" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="111:11-149:24" data-zylo-component="InspectionsPage"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="mx-auto max-w-4xl text-center"
          >
            <motion.div data-zylo-id="zylo-0112dc2a14d4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="117:13-122:26" data-zylo-component="InspectionsPage" variants={itemVariants} className="mb-6 inline-block">
              <span data-zylo-id="zylo-4e90e743258a" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="118:15-121:22" data-zylo-component="InspectionsPage" className="inline-flex items-center gap-2 rounded-full bg-secondary/20 px-4 py-2 text-sm font-semibold text-primary">
                <Home data-zylo-id="zylo-9ebbbe58df05" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="119:17-119:45" data-zylo-component="InspectionsPage" className="h-4 w-4" />
                Professional Home Inspections
              </span>
            </motion.div>

            <motion.h1 data-zylo-id="zylo-4914f54aed48" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="124:13-129:25" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl text-foreground"
            >
              Expert Home Inspections You Can Trust
            </motion.h1>

            <motion.p data-zylo-id="zylo-0b7cca60b507" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="131:13-136:24" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="mt-6 text-lg text-muted-foreground sm:text-xl"
            >
              Professional comprehensive inspections for buying, selling, or insurance purposes. Make informed decisions about your property.
            </motion.p>

            <motion.div data-zylo-id="zylo-50c5a5bcd8fb" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="138:13-148:26" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="mt-8 flex flex-col sm:flex-row gap-4 justify-center"
            >
              <Button data-zylo-id="zylo-374f25a892b4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="142:15-144:24" data-zylo-component="InspectionsPage" size="lg" asChild className="bg-primary hover:bg-primary/90">
                <Link data-zylo-id="zylo-fe154d331c2c" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="143:17-143:70" data-zylo-component="InspectionsPage" href="/inspections/book">Book Inspection</Link>
              </Button>
              <Button data-zylo-id="zylo-c6fd34d9ff11" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="145:15-147:24" data-zylo-component="InspectionsPage" size="lg" variant="outline" asChild>
                <Link data-zylo-id="zylo-307f2ac6e820" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="146:17-146:57" data-zylo-component="InspectionsPage" href="#services">Learn More</Link>
              </Button>
            </motion.div>
          </motion.div>

          {/* Hero Image */}
          <motion.div data-zylo-id="zylo-e459aa7023ed" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="152:11-163:24" data-zylo-component="InspectionsPage"
            variants={itemVariants}
            className="mt-12 relative"
          >
            <Image data-zylo-id="zylo-02c0ed211c5b" data-zylo-kind="media" data-zylo-cap="742" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="156:13-162:15" data-zylo-component="InspectionsPage"
              src="/images/generated/hero-professional-home-inspection.png"
              alt="Professional Home Inspection"
              width={1200}
              height={600}
              className="rounded-lg shadow-2xl"
            />
          </motion.div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section data-zylo-id="zylo-a1d5fb2b1546" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="168:7-214:17" data-zylo-component="InspectionsPage" className="bg-primary/5 py-16 sm:py-24">
        <div data-zylo-id="zylo-96401e5fee26" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="169:9-213:15" data-zylo-component="InspectionsPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-d6de7df921e5" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="170:11-189:24" data-zylo-component="InspectionsPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-3xl text-center mb-12"
          >
            <motion.h2 data-zylo-id="zylo-995fcf59a5ef" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="177:13-182:25" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl text-foreground mb-4"
            >
              Why Choose Blue Kraken Inspections?
            </motion.h2>
            <motion.p data-zylo-id="zylo-d7df0fda9325" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="183:13-188:24" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >
              Thorough, professional, and detailed home inspections
            </motion.p>
          </motion.div>

          <motion.div data-zylo-id="zylo-9f1dde2e46b8" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="191:11-212:24" data-zylo-component="InspectionsPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {whyChooseItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div data-zylo-id="zylo-7136615cedc1" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="201:17-209:30" data-zylo-component="InspectionsPage" key={index} variants={itemVariants}>
                  <Card data-zylo-id="zylo-746f8d0c7bd3" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="202:19-208:26" data-zylo-component="InspectionsPage" className="h-full border-0 shadow-md hover:shadow-lg transition-shadow">
                    <CardContent data-zylo-id="zylo-58177dd0b4bb" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="203:21-207:35" data-zylo-component="InspectionsPage" className="p-6">
                      <Icon data-zylo-id="zylo-3a66301b219e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="204:23-204:71" data-zylo-component="InspectionsPage" className="h-12 w-12 text-primary mb-4" />
                      <h3 data-zylo-id="zylo-2ba2339e398c" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="205:23-205:79" data-zylo-component="InspectionsPage" className="text-lg font-bold mb-2">{item.title}</h3>
                      <p data-zylo-id="zylo-f146d52b6b55" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="206:23-206:90" data-zylo-component="InspectionsPage" className="text-sm text-muted-foreground">{item.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </section>

      {/* Inspection Services */}
      <section data-zylo-id="zylo-8c1d3b882907" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="217:7-273:17" data-zylo-component="InspectionsPage" id="services" className="py-16 sm:py-24">
        <div data-zylo-id="zylo-dc426946dff0" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="218:9-272:15" data-zylo-component="InspectionsPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-09ce46874355" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="219:11-238:24" data-zylo-component="InspectionsPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-3xl text-center mb-12"
          >
            <motion.h2 data-zylo-id="zylo-93ec525032e8" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="226:13-231:25" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl text-foreground mb-4"
            >
              Inspection Services
            </motion.h2>
            <motion.p data-zylo-id="zylo-afbfceef4a9e" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="232:13-237:24" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-lg text-muted-foreground"
            >
              Choose the inspection service that fits your needs
            </motion.p>
          </motion.div>

          <motion.div data-zylo-id="zylo-1c4cc4231481" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="240:11-271:24" data-zylo-component="InspectionsPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="grid grid-cols-1 md:grid-cols-3 gap-6"
          >
            {inspectionTypes.map((service) => (
              <motion.div data-zylo-id="zylo-e943e2363c4a" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="248:15-269:28" data-zylo-component="InspectionsPage" key={service.id} variants={itemVariants}>
                <Card data-zylo-id="zylo-075fa82fc817" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="249:17-268:24" data-zylo-component="InspectionsPage" className="h-full border-0 shadow-lg hover:shadow-2xl transition-all">
                  <CardHeader data-zylo-id="zylo-d18d1752f2db" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="250:19-254:32" data-zylo-component="InspectionsPage" className="bg-gradient-to-r from-primary to-primary/80 text-white rounded-t-lg">
                    <CardTitle data-zylo-id="zylo-9308b4572773" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="251:21-251:79" data-zylo-component="InspectionsPage" className="text-xl">{service.title}</CardTitle>
                    <p data-zylo-id="zylo-704c4645bb70" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="252:21-252:88" data-zylo-component="InspectionsPage" className="text-sm text-white/90 mt-2">{service.description}</p>
                    <p data-zylo-id="zylo-fe29ae80995b" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="253:21-253:79" data-zylo-component="InspectionsPage" className="text-3xl font-bold mt-4">{service.price}</p>
                  </CardHeader>
                  <CardContent data-zylo-id="zylo-dbfbcd511f74" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="255:19-267:33" data-zylo-component="InspectionsPage" className="p-6">
                    <ul data-zylo-id="zylo-f4100525aa24" data-zylo-kind="list" data-zylo-cap="59" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="256:21-263:26" data-zylo-component="InspectionsPage" className="space-y-3 mb-6">
                      {service.features.map((feature, i) => (
                        <li data-zylo-id="zylo-710bb8d7a3dc" data-zylo-kind="list" data-zylo-cap="59" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="258:25-261:30" data-zylo-component="InspectionsPage" key={i} className="flex items-start gap-3">
                          <span data-zylo-id="zylo-082caf24be92" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="259:27-259:86" data-zylo-component="InspectionsPage" className="text-secondary font-bold text-lg">✓</span>
                          <span data-zylo-id="zylo-2b2e5ec877a9" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="260:27-260:85" data-zylo-component="InspectionsPage" className="text-sm text-foreground">{feature}</span>
                        </li>
                      ))}
                    </ul>
                    <Button data-zylo-id="zylo-e8096062005e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="264:21-266:30" data-zylo-component="InspectionsPage" asChild className="w-full bg-primary hover:bg-primary/90">
                      <Link data-zylo-id="zylo-2f50a34d1b69" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="265:23-265:69" data-zylo-component="InspectionsPage" href="/inspections/book">Book Now</Link>
                    </Button>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* What's Included */}
      <section data-zylo-id="zylo-f2e5994b2ffe" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="276:7-319:17" data-zylo-component="InspectionsPage" className="bg-foreground/5 py-16 sm:py-24">
        <div data-zylo-id="zylo-dc1d301b9e86" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="277:9-318:15" data-zylo-component="InspectionsPage" className="container mx-auto px-4 sm:px-6">
          <motion.div data-zylo-id="zylo-caa3c7408eb7" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="278:11-317:24" data-zylo-component="InspectionsPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
            className="mx-auto max-w-3xl"
          >
            <motion.h2 data-zylo-id="zylo-1aee1a4f2006" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="285:13-290:25" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-3xl font-bold text-foreground mb-12 text-center"
            >
              What's Included in Every Inspection
            </motion.h2>

            <div data-zylo-id="zylo-6d8552d47b2f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="292:13-316:19" data-zylo-component="InspectionsPage" className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[
                'Roof and foundation assessment',
                'Exterior walls and siding evaluation',
                'Windows and doors inspection',
                'Electrical system review',
                'Plumbing system assessment',
                'HVAC system inspection',
                'Interior walls and ceilings',
                'Floors and carpeting condition',
                'Kitchen appliances check',
                'Bathroom fixtures review',
                'Basement/crawl space evaluation',
                'Attic condition assessment'
              ].map((item, index) => (
                <motion.div data-zylo-id="zylo-d828426cba0b" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="307:17-314:30" data-zylo-component="InspectionsPage"
                  key={index}
                  variants={itemVariants}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 data-zylo-id="zylo-4aafefa7dbf7" data-zylo-kind="icon" data-zylo-cap="86" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="312:19-312:91" data-zylo-component="InspectionsPage" className="w-6 h-6 text-secondary flex-shrink-0 mt-0.5" />
                  <span data-zylo-id="zylo-bbe3d208f041" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="313:19-313:80" data-zylo-component="InspectionsPage" className="font-semibold text-foreground">{item}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section data-zylo-id="zylo-c747dc2c5221" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="322:7-353:17" data-zylo-component="InspectionsPage" className="bg-gradient-to-r from-primary to-primary/80 py-16 sm:py-24 text-white">
        <div data-zylo-id="zylo-4290dcffe28e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="323:9-352:15" data-zylo-component="InspectionsPage" className="container mx-auto px-4 sm:px-6 text-center">
          <motion.div data-zylo-id="zylo-d94744d168c4" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="324:11-351:24" data-zylo-component="InspectionsPage"
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-100px" }}
          >
            <motion.h2 data-zylo-id="zylo-91d7cc517d14" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="330:13-335:25" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-3xl font-bold sm:text-4xl mb-4"
            >
              Schedule Your Home Inspection Today
            </motion.h2>
            <motion.p data-zylo-id="zylo-1226eec3dd74" data-zylo-kind="text" data-zylo-cap="282" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="336:13-341:24" data-zylo-component="InspectionsPage"
              variants={itemVariants}
              className="text-lg text-white/90 mb-8"
            >
              Secure your appointment with just a $200 deposit.
            </motion.p>
            <motion.div data-zylo-id="zylo-a2c4c0b30b5e" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="342:13-350:26" data-zylo-component="InspectionsPage" variants={itemVariants}>
              <Button data-zylo-id="zylo-8966e06c9b61" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="343:15-349:24" data-zylo-component="InspectionsPage"
                size="lg"
                asChild
                className="bg-white text-primary hover:bg-gray-100"
              >
                <Link data-zylo-id="zylo-c9a7367ceea0" data-zylo-kind="link" data-zylo-cap="26" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="348:17-348:75" data-zylo-component="InspectionsPage" href="/inspections/book">Book Your Inspection</Link>
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      <Footer data-zylo-id="zylo-b5beb8e6131f" data-zylo-kind="container" data-zylo-cap="247" data-zylo-file="src/app/inspections/page.tsx" data-zylo-loc="355:7-355:17" data-zylo-component="InspectionsPage" />
    </>
  );
}
